// src/pages/Register.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
} from 'firebase/auth';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=1200&q=80';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const strengthMeta = [
  { label: '', color: 'bg-gray-300', text: 'text-gray-500' },
  { label: 'Very Weak', color: 'bg-red-500', text: 'text-red-500' },
  { label: 'Weak', color: 'bg-orange-500', text: 'text-orange-500' },
  { label: 'Fair', color: 'bg-yellow-500', text: 'text-yellow-600' },
  { label: 'Good', color: 'bg-lime-500', text: 'text-lime-600' },
  { label: 'Strong', color: 'bg-green-500', text: 'text-green-600' },
];

const SUCCESS_REDIRECT_DELAY_MS = 2500;

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const getPasswordStrength = (password) => {
  if (!password) return 0;
  let strength = 0;
  if (password.length >= 6) strength++;
  if (password.length >= 10) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  return Math.min(strength, 5);
};

const normalizePhone = (phone) => (phone || '').replace(/[\s()-]/g, '');

/* ================================================================== */
/* Register                                                            */
/* ================================================================== */

const Register = () => {
  const [step, setStep] = useState(1); // 1 = account, 2 = profile, 3 = review
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'member',
    phone: '',
    company: '',
    position: '',
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Once true, the entire form is locked. Used in addition to `loading`
  // so inputs stay disabled while the success banner is visible.
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const mountedRef = useRef(true);
  const redirectTimerRef = useRef(null);
  const navigate = useNavigate();

  const functions = useMemo(() => getFunctions(), []);
  const sendWelcomeEmail = useMemo(
    () => httpsCallable(functions, 'sendWelcomeEmail'),
    [functions],
  );

  /* ---------------- Mount guard ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, []);

  /* ---------------- Focus first field on mount ---------------- */
  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  /* ---------------- Focus first field of each step ---------------- */
  useEffect(() => {
    const t = setTimeout(() => {
      if (step === 1) nameInputRef.current?.focus();
      if (step === 2) phoneInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(t);
  }, [step]);

  /* ---------------- Clear error on user input ---------------- */
  const clearError = () => {
    if (error) setError('');
    if (success) setSuccess('');
  };

  /* ---------------- Field-level validation ---------------- */
  const validation = useMemo(() => {
    const v = {};
    if (form.name && form.name.trim().length < 2)
      v.name = 'Name must be at least 2 characters';
    if (form.email && !emailRegex.test(form.email))
      v.email = 'Enter a valid email address';
    if (form.password && form.password.length < 6)
      v.password = 'Password must be at least 6 characters';
    if (form.confirmPassword && form.password !== form.confirmPassword)
      v.confirmPassword = 'Passwords do not match';

    const digitsOnly = normalizePhone(form.phone).replace(/\D/g, '');
    if (form.phone && (digitsOnly.length < 7 || digitsOnly.length > 15))
      v.phone = 'Enter a valid phone number (7–15 digits)';

    return v;
  }, [form]);

  /* ---------------- Step gating ---------------- */
  const canProceedStep1 =
    form.name.trim().length >= 2 &&
    emailRegex.test(form.email) &&
    form.password.length >= 6 &&
    form.password === form.confirmPassword;

  const canSubmit = canProceedStep1 && agreeTerms;

  /* ---------------- Password strength ---------------- */
  const passwordStrength = useMemo(
    () => getPasswordStrength(form.password),
    [form.password],
  );
  const strength = strengthMeta[passwordStrength];

  /* ---------------- Step navigation ---------------- */
  const goNext = () => {
    if (submitted) return;
    if (step === 1 && !canProceedStep1) {
      setError('Please complete all required fields correctly.');
      return;
    }
    setError('');
    setStep((s) => Math.min(s + 1, 3));
  };

  const goBack = () => {
    if (submitted) return;
    setError('');
    setStep((s) => Math.max(s - 1, 1));
  };

  /* ---------------- Submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) return; // only final step submits
    if (submitted) return; // prevents any accidental double-submit
    clearError();

    if (!canSubmit) {
      if (!agreeTerms) {
        setError('Please agree to the Terms of Service to continue.');
      } else {
        setError('Please review your details and try again.');
      }
      return;
    }

    setLoading(true);
    let createdUser = null;

    try {
      // 1. Create the Auth user
      const userCred = await createUserWithEmailAndPassword(
        auth,
        form.email,
        form.password,
      );
      const user = userCred.user;
      createdUser = user;

      // 2. Set display name
      await updateProfile(user, {
        displayName: form.name.trim().slice(0, 100),
      });

      // 3. Create the Firestore profile doc
      const requestedRole = form.role === 'board' ? 'board' : 'member';
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: form.email,
        displayName: form.name.trim(),
        name: form.name.trim(),

        // Effective role starts as "member"; admin promotes later
        role: 'member',
        requestedRole,

        // Approval
        approved: false,
        isApproved: false,
        approvedBy: null,
        approvedAt: null,
        approvalNotes: null,
        rejectedAt: null,
        rejectionReason: null,

        // Registration
        registrationStatus: 'pending_approval',
        registrationComplete: true,
        registeredAt: serverTimestamp(),

        // Account
        isActive: true,
        isBlocked: false,
        isBanned: false,
        isAdmin: false,

        // Contact
        phone: form.phone.trim(),
        company: form.company.trim(),
        position: form.position.trim(),

        // Profile
        education: '',
        certifications: '',
        skills: '',
        experience: '',
        bio: '',
        photoUrl: null,

        // Privacy / preferences
        preferences: {
          emailNotifications: true,
          notifications: true,
        },
        profileVisibility: 'public',

        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: null,
        lastSeen: null,
      });

      // 4. Await the welcome email (best-effort).
      //    This must run BEFORE `signOut` — the callable function relies
      //    on `context.auth` to know who to email. If the user is already
      //    signed out, the call returns `unauthenticated`.
      try {
        await sendWelcomeEmail({
          uid: user.uid,
          email: form.email,
          name: form.name.trim(),
        });
      } catch (mailErr) {
        // Do not fail registration if the email fails.
        console.warn('Welcome email failed (ignored):', mailErr);
      }

      // 5. Sign out so user goes through the normal login flow.
      try {
        await signOut(auth);
      } catch {
        /* ignore */
      }

      if (!mountedRef.current) return;
      setSubmitted(true);
      setSuccess(
        'Registration successful! Your account is pending admin approval. ' +
          'Redirecting to login…',
      );
      redirectTimerRef.current = setTimeout(() => {
        if (mountedRef.current) navigate('/login');
      }, SUCCESS_REDIRECT_DELAY_MS);
    } catch (err) {
      console.error('Registration error:', err);

      // If we created the auth user but failed afterwards, sign out to
      // avoid a half-registered state.
      if (createdUser) {
        try {
          await signOut(auth);
        } catch {
          /* ignore */
        }
      }

      let msg = 'Registration failed. Please try again.';
      switch (err.code) {
        case 'auth/email-already-in-use':
          msg = 'Email already registered. Please log in or use a different email.';
          break;
        case 'auth/weak-password':
          msg = 'Password is too weak. Use at least 6 characters.';
          break;
        case 'auth/invalid-email':
          msg = 'Invalid email address format.';
          break;
        case 'auth/operation-not-allowed':
          msg = 'Email/password accounts are not enabled. Contact support.';
          break;
        default:
          break;
      }
      if (mountedRef.current) {
        setError(msg);
        setLoading(false);
      }
    }
  };

  /* ================================================================== */
  /* Step rendering                                                      */
  /* ================================================================== */

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="reg-name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          ref={nameInputRef}
          id="reg-name"
          type="text"
          placeholder="Juan Dela Cruz"
          value={form.name}
          onChange={(e) => {
            clearError();
            setForm({ ...form, name: e.target.value });
          }}
          disabled={submitted}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition disabled:opacity-60 ${
            validation.name
              ? 'border-red-400 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          }`}
          autoComplete="name"
          maxLength={100}
        />
        {validation.name && (
          <p className="text-xs text-red-500 mt-1">{validation.name}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="reg-email"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Email Address <span className="text-red-500">*</span>
        </label>
        <input
          ref={emailInputRef}
          id="reg-email"
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={(e) => {
            clearError();
            setForm({ ...form, email: e.target.value });
          }}
          disabled={submitted}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition disabled:opacity-60 ${
            validation.email
              ? 'border-red-400 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          }`}
          autoComplete="email"
        />
        {validation.email && (
          <p className="text-xs text-red-500 mt-1">{validation.email}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="reg-password"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="reg-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={form.password}
            onChange={(e) => {
              clearError();
              setForm({ ...form, password: e.target.value });
            }}
            disabled={submitted}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition pr-10 disabled:opacity-60 ${
              validation.password
                ? 'border-red-400 dark:border-red-500'
                : 'border-gray-300 dark:border-gray-600'
            }`}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-orange"
          >
            <i
              className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
              aria-hidden="true"
            />
          </button>
        </div>

        {form.password && (
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${strength.color}`}
                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                />
              </div>
              {strength.label && (
                <span className={`text-xs font-medium ${strength.text}`}>
                  {strength.label}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Use 8+ characters with uppercase, lowercase, numbers, and symbols
              for best security.
            </p>
          </div>
        )}
      </div>

      <div>
        <label
          htmlFor="confirm-password"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={form.confirmPassword}
            onChange={(e) => {
              clearError();
              setForm({ ...form, confirmPassword: e.target.value });
            }}
            disabled={submitted}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition pr-10 disabled:opacity-60 ${
              validation.confirmPassword
                ? 'border-red-400 dark:border-red-500'
                : 'border-gray-300 dark:border-gray-600'
            }`}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-orange"
          >
            <i
              className={`fas ${
                showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'
              }`}
              aria-hidden="true"
            />
          </button>
        </div>
        {validation.confirmPassword && (
          <p className="text-xs text-red-500 mt-1">
            {validation.confirmPassword}
          </p>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="phone"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Phone Number
        </label>
        <input
          ref={phoneInputRef}
          id="phone"
          type="tel"
          placeholder="+966 5X XXX XXXX"
          value={form.phone}
          onChange={(e) => {
            clearError();
            setForm({ ...form, phone: e.target.value });
          }}
          disabled={submitted}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition disabled:opacity-60 ${
            validation.phone
              ? 'border-red-400 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          }`}
          autoComplete="tel"
        />
        {validation.phone && (
          <p className="text-xs text-red-500 mt-1">{validation.phone}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="company"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Company/Organization
        </label>
        <input
          id="company"
          type="text"
          placeholder="Your company name"
          value={form.company}
          onChange={(e) => {
            clearError();
            setForm({ ...form, company: e.target.value });
          }}
          disabled={submitted}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition disabled:opacity-60"
          autoComplete="organization"
        />
      </div>

      <div>
        <label
          htmlFor="position"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Position/Title
        </label>
        <input
          id="position"
          type="text"
          placeholder="e.g., Electrical Engineer"
          value={form.position}
          onChange={(e) => {
            clearError();
            setForm({ ...form, position: e.target.value });
          }}
          disabled={submitted}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange focus:border-transparent dark:bg-gray-700 dark:text-white transition disabled:opacity-60"
          autoComplete="organization-title"
        />
      </div>

      <div>
        <label
          htmlFor="role-select"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Membership Type <span className="text-red-500">*</span>
        </label>
        <select
          id="role-select"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          disabled={submitted}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange dark:bg-gray-700 dark:text-white disabled:opacity-60"
        >
          <option value="member">Regular Member</option>
          <option value="board">
            Board of Directors (Requires Board Approval)
          </option>
        </select>
        {form.role === 'board' && (
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
            <i className="fas fa-info-circle mr-1" aria-hidden="true" />
            Board requests are reviewed separately and may take longer.
          </p>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white mb-3">
          Review your details
        </h3>
        <dl className="space-y-2 text-sm">
          <ReviewRow label="Name" value={form.name} />
          <ReviewRow label="Email" value={form.email} />
          <ReviewRow label="Phone" value={form.phone || '—'} />
          <ReviewRow label="Company" value={form.company || '—'} />
          <ReviewRow label="Position" value={form.position || '—'} />
          <ReviewRow
            label="Membership"
            value={
              form.role === 'board' ? 'Board of Directors' : 'Regular Member'
            }
          />
        </dl>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={agreeTerms}
          onChange={(e) => {
            clearError();
            setAgreeTerms(e.target.checked);
          }}
          disabled={submitted}
          className="mt-0.5 w-4 h-4 accent-orange disabled:opacity-60"
        />
        <span className="text-xs text-gray-600 dark:text-gray-400">
          I agree to the IIEE-ERCSA{' '}
          <Link to="/terms" className="text-orange font-medium hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-orange font-medium hover:underline">
            Privacy Policy
          </Link>
          , and I confirm that the information provided is accurate.
        </span>
      </label>

      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="flex items-start gap-2">
          <i
            className="fas fa-clock text-yellow-600 dark:text-yellow-400 text-sm mt-0.5"
            aria-hidden="true"
          />
          <div className="text-xs text-gray-700 dark:text-gray-300">
            <p className="font-semibold mb-1">⚠️ Admin Approval Required</p>
            <p>
              All new registrations require admin approval before you can access
              the full system. While pending, you can log in and update your
              profile. You'll receive an email once approved — usually within
              24–48 hours.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  /* ================================================================== */
  /* Render                                                              */
  /* ================================================================== */

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* ============ LEFT: Hero (desktop) ============ */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange/80 via-orange-dark/70 to-black/80" />
        <div className="absolute inset-0 bg-black/30" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div className="flex items-center gap-3">
            <img
              src="/images/iiee-logo.png"
              alt="IIEE-ERCSA"
              className="h-12 w-12 object-contain"
            />
            <div>
              <h2 className="text-lg font-bold">IIEE-ERCSA</h2>
              <p className="text-xs text-white/70">Eastern Region Chapter</p>
            </div>
          </div>

          <div className="max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold mb-4 leading-tight">
              Join the <span className="text-orange">engineering community</span>
            </h1>
            <p className="text-base xl:text-lg text-white/80 mb-8">
              Be part of a growing network of electrical engineers. Share, learn,
              and connect with professionals across the Eastern Region.
            </p>

            <ul className="space-y-3">
              <FeatureBullet
                icon="fa-user-check"
                text="Verified membership with admin approval"
              />
              <FeatureBullet
                icon="fa-users"
                text="Connect with 500+ engineering professionals"
              />
              <FeatureBullet
                icon="fa-calendar-check"
                text="Access events, awards, and resources"
              />
            </ul>
          </div>

          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} IIEE-ERCSA · Eastern Region Chapter
          </p>
        </div>
      </div>

      {/* ============ RIGHT: Form ============ */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center px-4 sm:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden text-center mb-6">
            <img
              src="/images/iiee-logo.png"
              alt="IIEE-ERCSA"
              className="h-14 mx-auto mb-3"
            />
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                Join IIEE-ERCSA
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                Create your account in 3 easy steps
              </p>
            </div>

            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      s < step
                        ? 'bg-orange text-white'
                        : s === step
                        ? 'bg-orange text-white ring-4 ring-orange/20'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}
                  >
                    {s < step ? (
                      <i className="fas fa-check text-xs" aria-hidden="true" />
                    ) : (
                      s
                    )}
                  </div>
                  {s < 3 && (
                    <div
                      className={`h-0.5 w-10 transition-colors ${
                        s < step ? 'bg-orange' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Step {step} of 3 ·{' '}
                {step === 1
                  ? 'Account Setup'
                  : step === 2
                  ? 'Profile Details'
                  : 'Review & Confirm'}
              </p>
            </div>

            {/* Error / Success */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  role="alert"
                  className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 rounded-lg text-sm flex items-start gap-2"
                >
                  <i
                    className="fas fa-exclamation-circle mt-0.5"
                    aria-hidden="true"
                  />
                  <span>{error}</span>
                </motion.div>
              )}

              {success && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  role="status"
                  className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-400 text-green-700 dark:text-green-300 rounded-lg text-sm flex items-start gap-2"
                >
                  <i
                    className="fas fa-check-circle mt-0.5"
                    aria-hidden="true"
                  />
                  <span>{success}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                // Enter on steps 1/2 → advance
                if (e.key === 'Enter' && step < 3 && !loading && !submitted) {
                  e.preventDefault();
                  goNext();
                }
              }}
              className="space-y-4"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex gap-3 pt-2">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={goBack}
                    disabled={loading || submitted}
                    className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium disabled:opacity-50"
                  >
                    <i className="fas fa-arrow-left mr-2" aria-hidden="true" />
                    Back
                  </button>
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={goNext}
                    disabled={(step === 1 && !canProceedStep1) || submitted}
                    className="flex-1 bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                    <i className="fas fa-arrow-right ml-2" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading || !canSubmit || submitted}
                    className="flex-1 bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading || submitted ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                        Creating...
                      </span>
                    ) : (
                      <>
                        <i className="fas fa-user-plus mr-2" aria-hidden="true" />
                        Register
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-orange hover:underline font-semibold transition"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Small components                                                    */
/* ================================================================== */

const FeatureBullet = ({ icon, text }) => (
  <li className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center flex-shrink-0">
      <i className={`fas ${icon} text-white text-sm`} aria-hidden="true" />
    </div>
    <span className="text-sm text-white/90">{text}</span>
  </li>
);

const ReviewRow = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-gray-800 dark:text-white text-right break-all">
      {value}
    </dd>
  </div>
);

export default Register;